import type { GlobalHit, PersonHit } from "@/components/AlgoliaSearch";
import type { QuickAddPersonProgramStatusNode, QuickAddPersonProgramStatusResponse } from "./queries";

/** `null` = still loading. `"unavailable"` = lookup failed; keep Algolia people. */
export type PersonProgramStatusLookup = ReadonlyMap<string, string | null> | null | "unavailable";

export function algoliaPersonPgaId(hit: PersonHit): string | null {
  return hit.pga_id || hit.legacy_id || null;
}

export function isAlgoliaPersonHit(hit: GlobalHit): hit is PersonHit {
  return hit.object_type === "Person";
}

export function uniqueAlgoliaPersonPgaIds(hits: GlobalHit[]): string[] {
  return [
    ...new Set(
      hits
        .filter(isAlgoliaPersonHit)
        .map(algoliaPersonPgaId)
        .filter((id): id is string => Boolean(id))
    )
  ];
}

export function hasProgramStatus(status: string | null | undefined): boolean {
  return Boolean(status?.trim());
}

export function programStatusByPgaId(nodes: QuickAddPersonProgramStatusNode[] | undefined): Map<string, string | null> {
  const byId = new Map<string, string | null>();
  for (const node of nodes ?? []) {
    byId.set(node.pgaId, node.latestProgram?.status ?? null);
  }
  return byId;
}

export function resolvePersonStatusLookup(args: {
  personPgaIdCount: number;
  loading: boolean;
  data: QuickAddPersonProgramStatusResponse | undefined;
  error: unknown;
}): PersonProgramStatusLookup {
  if (args.personPgaIdCount === 0) return new Map();
  if (args.loading) return null;
  const nodes = args.data?.people?.nodes;
  if (nodes) return programStatusByPgaId(nodes);
  if (args.error || args.data) return "unavailable";
  return new Map();
}

/** Drop Person hits with a known blank membership program status. Facilities stay. `null` = still loading. `"unavailable"` = fail open. */
export function filterHitsMissingProgramStatus(
  hits: GlobalHit[],
  statusByPgaId: PersonProgramStatusLookup
): GlobalHit[] {
  if (statusByPgaId === "unavailable") return hits;
  if (statusByPgaId === null) {
    return hits.filter((hit) => !isAlgoliaPersonHit(hit));
  }
  return hits.filter((hit) => {
    if (!isAlgoliaPersonHit(hit)) return true;
    const id = algoliaPersonPgaId(hit);
    if (!id) return false;
    if (!statusByPgaId.has(id)) return true;
    return hasProgramStatus(statusByPgaId.get(id));
  });
}
